import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { Policy } from '../lib/api';
import { Empty, ErrorNotice, Loading, Notice, Panel, formatDateTime, useAsync } from '../components/ui';

export default function PoliciesPage() {
  const { data, error, loading } = useAsync(
    (signal) => api.get<{ policies: Policy[] }>('/policies', signal),
    [],
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Policies</h2>
          <p>What a device is allowed to do, and which app it locks into.</p>
        </div>
        <Link className="button primary" to="/policies/new">New policy</Link>
      </div>

      <ErrorNotice error={error} />

      <Notice kind="info">
        <strong>Test before you tighten.</strong>
        <div>
          A production policy that has not been validated on real hardware can only be assigned to
          one device at a time. Prove it on a single tablet, confirm the device still checks in,
          then mark it validated to release it to the fleet.
        </div>
      </Notice>

      <Panel>
        {loading && !data ? (
          <Loading what="policies" />
        ) : (data?.policies.length ?? 0) === 0 ? (
          <Empty>No policies defined.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Kind</th><th className="num">Version</th>
                  <th>Kiosk</th><th>Restrictions</th><th>Validated</th><th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {data!.policies.map((p) => {
                  const activeRestrictions = Object.values(p.definition.restrictions ?? {}).filter(Boolean).length;
                  return (
                    <tr key={p.id}>
                      <td>
                        <Link to={`/policies/${p.id}`}>{p.name}</Link>
                        {p.is_default && <span className="badge info" style={{ marginLeft: 8 }}>DEFAULT</span>}
                        <div className="small muted">{p.description}</div>
                      </td>
                      <td>
                        <span className={`badge ${p.kind === 'production' ? 'danger' : 'muted'}`}>
                          {p.kind.toUpperCase()}
                        </span>
                      </td>
                      <td className="num">{p.version}</td>
                      <td>
                        {p.definition.kiosk?.enabled
                          ? <>
                              <span className="badge ok">ON</span>
                              <div className="small muted mono">{p.definition.kiosk.kioskPackage}</div>
                            </>
                          : <span className="badge muted">OFF</span>}
                      </td>
                      <td className="num">{activeRestrictions}</td>
                      <td>
                        {p.validated_at
                          ? <span className="badge ok">VALIDATED</span>
                          : <span className="badge warn">NOT VALIDATED</span>}
                      </td>
                      <td className="small muted">{formatDateTime(p.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
